import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Plane, Ship, Truck, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const types = [
  { id: "aircraft", label: "Aircraft", icon: Plane },
  { id: "boat", label: "Boat / Marine", icon: Ship },
  { id: "rv", label: "RV / Motorhome", icon: Truck },
  { id: "other", label: "Something else", icon: Sparkles },
];

export default function QuoteRequest() {
  const [type, setType] = useState("aircraft");
  const [form, setForm] = useState({
    item_description: "",
    name: "",
    email: "",
    phone: "",
    preferred_timeline: "",
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_description || !form.name || !form.email || !form.phone) {
      return toast.error("Please fill in the required fields");
    }
    setSending(true);
    try {
      await api.post("/quotes", { item_type: type, ...form });
      toast.success("Quote request sent — we'll be in touch soon.");
      setDone(true);
    } catch {
      toast.error("Could not send request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ item_description: "", name: "", email: "", phone: "", preferred_timeline: "", notes: "" });
    setType("aircraft");
  };

  return (
    <section id="quote" className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10" data-testid="quote-section">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.3em] uppercase text-primary mb-4">Beyond auto &amp; home</p>
          <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none text-foreground mb-8">
            Got something<br /><span className="italic text-primary">unusual?</span>
          </h2>
          <p className="text-muted-foreground font-light text-lg leading-relaxed max-w-md mb-8">
            Aircraft, boats, RVs, motorhomes, luxury bikes, private jets — if it can be detailed, we&apos;ll take a look. Tell us about it and we&apos;ll come back with a custom quote.
          </p>
          <ul className="space-y-3 text-sm text-foreground/70">
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-primary" /> Same obsessive standard as our auto &amp; home details</li>
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-primary" /> On-site service across the Gallatin Valley</li>
            <li className="flex items-center gap-3"><span className="w-6 h-px bg-primary" /> Response within 24 hours</li>
          </ul>
        </div>

        <div className="lg:col-span-7 bg-card border gold-border clip-frame p-8 sm:p-10 lg:p-12">
          {done ? (
            <div className="text-center py-8" data-testid="quote-confirmation">
              <h3 className="font-serif text-4xl text-foreground mb-4">Request received</h3>
              <p className="text-muted-foreground font-light mb-8 max-w-md mx-auto">Thanks, {form.name.split(" ")[0]}. We&apos;ll review the details and get back to you within 24 hours.</p>
              <button onClick={reset} data-testid="quote-new-btn" className="px-8 py-3 border border-primary/40 text-primary text-sm tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors clip-frame">
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form">
              <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">What do you need detailed?</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                {types.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setType(t.id)}
                    data-testid={`quote-type-${t.id}`}
                    className={`flex flex-col items-center gap-2 p-4 border clip-frame transition-colors ${type === t.id ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
                  >
                    <t.icon className="w-5 h-5" />
                    <span className="text-xs tracking-wide">{t.label}</span>
                  </button>
                ))}
              </div>

              <motion.div layout className="grid gap-4">
                <Textarea
                  placeholder="Tell us about the item — make/model, size, condition, what you want detailed"
                  value={form.item_description}
                  onChange={(e) => setForm({ ...form, item_description: e.target.value })}
                  data-testid="quote-description"
                  className="bg-background border-border min-h-32"
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-name" className="bg-background border-border h-12" />
                  <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-phone" className="bg-background border-border h-12" />
                </div>
                <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-email" className="bg-background border-border h-12" />
                <Input placeholder="Preferred timeline (optional)" value={form.preferred_timeline} onChange={(e) => setForm({ ...form, preferred_timeline: e.target.value })} data-testid="quote-timeline" className="bg-background border-border h-12" />
                <Textarea placeholder="Anything else we should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="quote-notes" className="bg-background border-border min-h-24" />

                <button type="submit" disabled={sending} data-testid="quote-submit-btn"
                  className="mt-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame flex items-center justify-center gap-2 disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Request Quote
                </button>
              </motion.div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
